import { useCallback } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/api/auth-client";
import { expenseAdminTokenSource } from "@/auth";
import { clearSessionScopedStorage } from "@/constants/storage-keys";
import { useAppDispatch } from "@/store";
import { clearCredentials } from "@/store/auth.slice";

export interface LogoutOptions {
    /** Optional message surfaced on the login page after redirecting. */
    message?: string;
}

/**
 * Centralizes the logout flow so every caller clears auth state consistently:
 * revoke the session server-side (best effort), drop local credentials and
 * session-scoped storage, notify the RBAC token source so permission state
 * clears at once, and redirect to /login.
 *
 * When `message` is provided it is passed through the navigation state so the
 * login page can explain why the session ended (e.g. lack of permissions).
 */
export function useLogout(): (options?: LogoutOptions) => Promise<void> {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    return useCallback(
        async ({ message }: LogoutOptions = {}) => {
            // Revoke first so the refresh cookie can't silently re-authenticate.
            try {
                await authClient.logout();
            } finally {
                dispatch(clearCredentials());
                clearSessionScopedStorage();
                // Clear RBAC state immediately instead of waiting for the poll interval.
                expenseAdminTokenSource.notifyTokenChange();
                navigate("/login", message ? { replace: true, state: { message } } : { replace: true });
            }
        },
        [dispatch, navigate],
    );
}
